<template>
  <div class="w-100">
    <h1>Categories Management</h1>

    <!-- ── Category Groups ── -->
    <section class="groups-panel">
      <div class="groups-panel__header">
        <div>
          <div class="groups-panel__title">
            <i class="fa fa-layer-group"></i> Category Groups
          </div>
          <div class="groups-panel__subtitle">
            {{ groups.length }} group{{ groups.length === 1 ? '' : 's' }} — click a group to edit it
          </div>
        </div>
        <div class="groups-panel__actions">
          <translator
              :items="groups"
              :keys="groupTranslateKeys"
              model="CategoryGroup"
              modal-name="group-translate-modal"
          >
            <template #default="{ clicked }">
              <button class="btn-secondary-action" type="button" @click="clicked">
                <i class="fa fa-language"></i> Translate groups
              </button>
            </template>
          </translator>
          <button class="btn-primary-action" type="button" @click="showGroupCreateModal">
            <i class="fa fa-plus"></i> New group
          </button>
        </div>
      </div>

      <div v-if="groupsLoading" class="groups-empty">
        <i class="fa fa-spinner fa-spin"></i> Loading groups...
      </div>
      <div v-else-if="!groups.length" class="groups-empty">
        <i class="fa fa-layer-group"></i>
        <span>No category groups yet. Create the first one to group your categories.</span>
      </div>
      <div v-else class="groups-grid">
        <div
            v-for="group in groups"
            :key="group.id"
            class="group-card"
            @click="showGroupUpdateModal(group)"
        >
          <div class="group-card__top">
            <span class="group-card__title">{{ group.title }}</span>
            <span
                v-if="group.status !== undefined && group.status !== null"
                :class="['group-status', groupStatusClass(group)]"
            >{{ groupStatusLabel(group) }}</span>
          </div>
          <div class="group-card__meta">
            <span v-if="group.slug"><i class="fa fa-hashtag"></i> {{ group.slug }}</span>
            <span v-if="group.priority !== undefined && group.priority !== null">
              <i class="fa fa-sort"></i> priority {{ group.priority }}
            </span>
          </div>
          <div class="group-card__footer">
            <i class="fa fa-pencil"></i> Edit
          </div>
        </div>
      </div>
    </section>

    <div class="col-lg-12">
      <div class="row">
        <div class="col-lg-9">
          <div class="row">
            <div id="items" class="row mt-3">
              <div
                  class="item col-lg-3"
                  v-for="item in items"
                  v-bind:key="item.id"
                  :style="getPhotoUrl(item)"
                  @click="showUpdateModal(item)"
              >
                <div style="position: relative">
                  <i v-if="item.status === 1" class="alert alert-success">Active</i>
                  <i v-else-if="item.status === 0" class="alert alert-danger">Deactivated</i>
                  <i v-else class="alert alert-primary">Hidden</i>
                </div>
                <div class="item-overlay">
                  {{ item.title }}<br />
                  <i class="fa fa-hashtag"></i> {{ item.slug }}
                  <br>
                  <div>
                    mapped to :
                    <ul v-for="category in item.mapped_categories" :key="category.id">
                      <li>{{category.title}}</li>
                    </ul>
                  </div>


                </div>
              </div>

              <div class="item col-lg-3 add-item" @click="showCreateModal">
                <div class="text-center">
                  <i class="fa fa-plus"></i><br />
                  Create a category
                </div>
              </div>
            </div>
          </div>

        </div>
        <div class="col-lg-3">
            <translator
                :items="items"
                :keys="translateKeys"
                model="Category"
            >
              <template #default="{ clicked }">
                <div class="add-item" @click="clicked">
                  <div class="text-center">
                    <i class="fa fa-language"></i><br />
                    Create new or update Translate
                  </div>
                </div>
              </template>

            </translator>
        </div>
      </div>

    </div>


    <modal name="create-modal" height="auto">
      <div class="p-4">
        <div class="h3 mb-3">Create a category</div>
        <form action="">
          <div class="col-lg-12">
            <label>title</label>
            <input
                v-model="createForm.title"
                type="text"
                class="form-control"
                placeholder="Title"
                @input="generateSlug"
                required
            />
          </div>
          <div class="col-lg-12">
            <label>Slug</label>
            <input
                v-model="createForm.slug"
                type="text"
                class="form-control"
                placeholder="Uri slug"
                required
            />
            <span style="color: red">Slug generated automatically by title, but if you want you can modify it</span>
          </div>

          <div class="col-lg-12">
            <label>Description</label>
            <vue-editor
                v-model="createForm.description"
                placeholder="Description"
            ></vue-editor>
          </div>
          <div class="form-group col-lg-12">
            <label>mapped connect categories</label>
            <multiselect
                class="form-control"
                v-model="selectedCategories"
                :options="connectCategories"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick some"
                label="title"
                track-by="id"
                :preselect-first="false">
            </multiselect>
          </div>
          <div class="form-group col-lg-12">
            <label>status</label>
            <multiselect
                class="form-control"
                v-model="status"
                :options="statusList"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick some"
                label="title"
                track-by="id"
                :preselect-first="false">
            </multiselect>
          </div>
          <div class="form-group col-lg-12">
            <div class="mb-2 mt-3">
              <label for="formFile" class="form-label">Select a photo...</label>
              <input
                  class="form-control"
                  type="file"
                  id="formFile"
                  @change="createFormUpload"
                  ref="createFormUpload"
              />
            </div>
            <div v-if="createForm.photo">
              <img width="100" height="100" :src="createForm.photo"></img>
            </div>
            <div v-if="createForm.photo">
              <i class="fa fa-check"></i> Photo uploaded!
            </div>
          </div>
          <div class="col-lg-12">
            <label>Showing priority</label>
            <input
                v-model="createForm.priority"
                type="text"
                class="form-control"
                placeholder="priority..."
                required
            />
          </div>
          <div class="col-lg-12">
            <label>Category group</label>
            <select v-model="createForm.category_group_id" class="form-control">
              <option :value="null">— none —</option>
              <option v-for="group in groups" :key="group.id" :value="group.id">
                {{ group.title }}
              </option>
            </select>
          </div>


          <ul style="padding-left: 20px; margin-top: 20px">
            <li
              v-for="error in createForm.errors"
              v-bind:key="error"
              class="text-danger"
            >
              {{ error }}
            </li>
          </ul>

          <button :disabled="createBtnDisabled" class="btn btn-primary mt-3 w-100" @click="createCategory">
            {{createBtnText}}
          </button>
        </form>
      </div>
    </modal>

    <modal name="update-modal" height="auto">
      <div class="p-4">
        <div class="h3 mb-3">Update a category</div>
        <form action="">
          <div class="col-lg-12">
            <label>title</label>
            <input
              v-model="updateForm.title"
              type="text"
              class="form-control"
              placeholder="Title"
              required

            />
          </div>
          <div class="col-lg-12">
            <label>Slug</label>
            <input
                v-model="updateForm.slug"
                type="text"
                class="form-control"
                placeholder="Uri slug"
                required
                @keyup="updateSlug"
            />
            <span style="color: red">Slug generated automatically by title, but if you want you can modify it</span>
          </div>
          <div class="col-lg-12">
            <label>Description</label>

            <vue-editor
                v-model="updateForm.description"
                placeholder="Description"
            ></vue-editor>
          </div>
          <div class="form-group col-lg-12">
            <label>mapped connect categories</label>
            <multiselect
                class="form-control"
                v-model="selectedCategories"
                :options="connectCategories"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick some"
                label="title"
                track-by="id"
                :preselect-first="false">
            </multiselect>
          </div>
          <div class="form-group col-lg-12">
            <label>status</label>
            <multiselect
                class="form-control"
                v-model="status"
                :options="statusList"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick some"
                label="title"
                track-by="id"
                :preselect-first="false">
            </multiselect>
          </div>
          <div class="col-lg-12">
            <div class="mb-2 mt-3">
              <label for="formFile" class="form-label">Select a photo...</label>
              <input
                class="form-control"
                type="file"
                id="formFile"
                @change="updateFormUpload"
                ref="updateFormUpload"
              />
            </div>
            <div v-if="updateForm.photo">
              <img width="100" height="100" :src="updateForm.photo" />
            </div>
            <div v-if="updateForm.photo">
              <i class="fa fa-check"></i> Photo uploaded!
            </div>
          </div>
          <div class="col-lg-12">
            <label>Showing priority</label>
            <input
                v-model="updateForm.priority"
                type="text"
                class="form-control"
                placeholder="priority..."
                required
            />
          </div>
          <div class="col-lg-12">
            <label>Category group</label>
            <select v-model="updateForm.category_group_id" class="form-control">
              <option :value="null">— none —</option>
              <option v-for="group in groups" :key="group.id" :value="group.id">
                {{ group.title }}
              </option>
            </select>
          </div>

          <ul style="padding-left: 20px; margin-top: 20px">
            <li
              v-for="error in updateForm.errors"
              v-bind:key="error"
              class="text-danger"
            >
              {{ error }}
            </li>
          </ul>

          <button :disabled="updateBtnDisabled" class="btn btn-primary mt-3 w-100" @click="updateCategory">
            {{updateBtnText}}
          </button>
          <button class="btn btn-danger mt-2 w-100" @click="deleteCategory">
            Delete
          </button>
        </form>
      </div>
    </modal>

    <modal name="group-create-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Create a category group</span>
          <button class="dd-modal__close" type="button" @click="$modal.hide('group-create-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-field mb-3">
            <label class="filter-label">Title <span class="required">*</span></label>
            <input
                v-model="groupCreateForm.title"
                type="text"
                class="dd-input"
                placeholder="Group title"
                @input="generateGroupSlug"
            />
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Slug</label>
            <input
                v-model="groupCreateForm.slug"
                type="text"
                class="dd-input"
                placeholder="Group slug"
            />
            <span class="field-hint">Generated from the title, you can change it.</span>
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Status</label>
            <select v-model.number="groupCreateForm.status" class="dd-input">
              <option v-for="state in statusList" :key="state.id" :value="state.id">{{ state.title }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="filter-label">Showing priority</label>
            <input
                v-model.number="groupCreateForm.priority"
                type="number"
                class="dd-input"
                placeholder="priority..."
            />
          </div>
          <ul class="error-list" v-if="groupCreateForm.errors.length">
            <li v-for="error in groupCreateForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" type="button" @click="$modal.hide('group-create-modal')">Cancel</button>
          <button class="btn-submit" type="button" :disabled="groupCreateBtnDisabled" @click.prevent="createGroup">
            <i class="fa fa-plus"></i> {{ groupCreateBtnDisabled ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </modal>

    <modal name="group-update-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Edit category group</span>
          <button class="dd-modal__close" type="button" @click="$modal.hide('group-update-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-field mb-3">
            <label class="filter-label">Title <span class="required">*</span></label>
            <input v-model="groupUpdateForm.title" type="text" class="dd-input" placeholder="Group title" />
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Slug</label>
            <input
                v-model="groupUpdateForm.slug"
                type="text"
                class="dd-input"
                placeholder="Group slug"
                @keyup="updateGroupSlug"
            />
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Status</label>
            <select v-model.number="groupUpdateForm.status" class="dd-input">
              <option v-for="state in statusList" :key="state.id" :value="state.id">{{ state.title }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="filter-label">Showing priority</label>
            <input v-model.number="groupUpdateForm.priority" type="number" class="dd-input" placeholder="priority..." />
          </div>
          <ul class="error-list" v-if="groupUpdateForm.errors.length">
            <li v-for="error in groupUpdateForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-delete" type="button" @click.prevent="deleteGroup">
            <i class="fa fa-trash"></i> Delete
          </button>
          <div style="flex: 1"></div>
          <button class="btn-cancel" type="button" @click="$modal.hide('group-update-modal')">Cancel</button>
          <button class="btn-submit" type="button" :disabled="groupUpdateBtnDisabled" @click.prevent="updateGroup">
            <i class="fa fa-check"></i> {{ groupUpdateBtnDisabled ? 'Saving...' : 'Update' }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { config,saveTranslate } from "../helpers";
import multiselect from 'vue-multiselect'
import Translator from "@/components/Translator.vue";
import { VueEditor } from "vue2-editor";
export default {
  name: "Categories",
  components: {multiselect, Translator, VueEditor,},
  data() {
    return {
      connectCategories : [],
      selectedCategories : [],
      translateKeys: [
          {
            "key" : "title",
            "type": "text",
          },
        {
          "key" : "description",
          "type": "text",
        },
      ],
      url: "",
      items: [],
      groupsUrl: "",
      groups: [],
      groupsLoading: false,
      groupTranslateKeys: [
        {
          "key": "title",
          "type": "text",
        },
      ],
      groupCreateForm: {
        title: "",
        slug: "",
        status: 1,
        priority: 0,
        errors: [],
      },
      groupUpdateForm: {
        id: null,
        title: "",
        slug: "",
        status: 1,
        priority: 0,
        errors: [],
      },
      groupCreateBtnDisabled: false,
      groupUpdateBtnDisabled: false,
      status: '',
      statusList: [
        {
          "id" : 1,
          "title": "Active",
        },
        {
          "id" : 2,
          "title": "Hidden",
        },
        {
          "id" : 0,
          "title": "Deactivate",
        }
      ],
      createForm: {
        title: "",
        slug: "",
        description: "",
        photo: "",
        priority: "",
        category_group_id: null,

      },
      updateForm: {
        id: null,
        title: "",
        slug: "",
        description: "",
        priority: "",
        status: "",
        mapped_categories : [],
        photo: "",
        category_group_id: null,
      },
      updateBtnText : 'update',
      updateBtnDisabled: false,
      createBtnText : 'create',
      createBtnDisabled: false,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/categories";
    this.groupsUrl = config("BACKEND_ROOT") + "/admin/category-groups";
    this.loadData();
    this.loadGroups();
    this.loadConnectCategories();
  },

  methods: {
    generateSlug(){
      this.createForm.slug = this.createForm.title.replaceAll(' ', '-')
    },
    updateSlug(){
      this.updateForm.slug = this.updateForm.slug.replaceAll(' ', '-')
    },

    loadData() {
      axios.get(this.url).then((response) => {
        this.items = response.data.data;
      });
    },
    loadConnectCategories(){
      axios.get(this.url+"/connect-list").then((response) => {
        this.connectCategories = response.data.data;
      });
    },

    createCategory(event) {
      event.preventDefault();
      this.createForm.status = this.status.id;
      this.createForm.mapped_categories = this.selectedCategories.map(cat => cat.id);
      axios
        .post(this.url, this.createForm)
        .then((response) => {
          location.reload();
        })
        .catch((error) => {
          this.createForm.errors = Object.values(
            error.response.data.errors
          ).flat();
        });
    },

    updateCategory(event)
    {
      event.preventDefault();
      this.updateForm.status = this.status.id;
      this.updateForm.mapped_categories = this.selectedCategories.map(cat => cat.id);

      axios
        .put(this.url + '/' + this.updateForm.id, this.updateForm)
        .then((response) => {
          location.reload();
        })
        .catch((error) => {
          this.updateForm.errors = Object.values(
            error.response.data.errors
          ).flat();
        });
    },

    deleteCategory(event) {
      event.preventDefault();
      const confirm = prompt('Do you want to delete this category? Enter 8:');
      
      if (confirm === 8) {
        axios
        .delete(this.url + '/' + this.updateForm.id)
        .then((response) => {
          location.reload();
        });
      }
    },

    showCreateModal() {
      this.selectedCategories = [];
      this.$modal.show("create-modal");
    },

    showUpdateModal(item) {
      this.selectedCategories = [];
      this.updateForm = { id: null, title: "", slug: "", description: "", photo: "", category_group_id: null };
      this.updateForm.id = item.id;
      this.updateForm.photo = item.photo;
      this.updateForm.title = item.title;
      this.updateForm.slug = item.slug;
      this.updateForm.description = item.description;
      this.status = this.statusList.filter(state => item.status === state.id)[0];
      this.updateForm.priority = item.priority;
      this.updateForm.mapped_categories = item.mapped_categories;
      this.updateForm.category_group_id = item.category_group_id || null;
      this.selectedCategories = item.mapped_categories;
      this.$modal.show("update-modal");
    },

    createFormUpload() {

      this.createBtnDisabled = true;
      this.createBtnText = "Wait to upload file";
      const formData = new FormData();
      formData.append("file", this.$refs.createFormUpload.files[0]);
      formData.append("type", this.$refs.createFormUpload.files[0].type.split('/')[0]);
      const headers = { "Content-Type": "multipart/form-data" };

      axios
        .post(config("BACKEND_ROOT") + "/admin/uploads", formData, { headers })
        .then((res) => {
          this.createForm.photo = res.data.data.url;
          this.createBtnDisabled = false;
          this.createBtnText = "create";
        })
        .catch((error) => {
          this.createForm.errors = error.response.data.errors.file;
          this.createBtnDisabled = false;
          this.createBtnText = "create";
        });
    },

    updateFormUpload() {
      this.updateBtnDisabled = true;
      this.updateBtnText = "Wait to upload file";
      const formData = new FormData();
      formData.append("file", this.$refs.updateFormUpload.files[0]);
      formData.append("type", this.$refs.updateFormUpload.files[0].type.split('/')[0]);
      const headers = { "Content-Type": "multipart/form-data" };
      axios
        .post(config("BACKEND_ROOT") + "/admin/uploads", formData, { headers })
        .then((res) => {
          this.updateForm.photo = res.data.data.url;
          this.updateBtnDisabled = false;
          this.updateBtnText = "Update";
        })
        .catch((error) => {
          this.updateForm.errors = error.response.data.errors.file;
          this.updateBtnDisabled = false;
          this.updateBtnText = "Update";
        });
    },

    getPhotoUrl(item) {
      if (!item.photo) {
        return "";
      }

      return `background:url("${item.photo.url}")`;
    },

    /* ── Category groups ── */
    loadGroups() {
      this.groupsLoading = true;
      axios
        .get(this.groupsUrl)
        .then((response) => {
          const data = response.data.data;
          this.groups = Array.isArray(data) ? data : (data && data.data) || [];
        })
        .catch(() => {
          this.groups = [];
        })
        .finally(() => {
          this.groupsLoading = false;
        });
    },

    generateGroupSlug() {
      this.groupCreateForm.slug = (this.groupCreateForm.title || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");
    },

    updateGroupSlug() {
      this.groupUpdateForm.slug = (this.groupUpdateForm.slug || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");
    },

    groupStatusLabel(group) {
      const state = this.statusList.filter((item) => item.id === group.status)[0];
      return state ? state.title : "Unknown";
    },

    groupStatusClass(group) {
      return group.status === 1 ? "group-status--active" : "group-status--inactive";
    },

    groupErrors(error) {
      const errors = error && error.response && error.response.data && error.response.data.errors;
      if (errors && Object.keys(errors).length) {
        return Object.values(errors).flat();
      }
      const message =
        (error && error.response && error.response.data && error.response.data.meta && error.response.data.meta.message) ||
        (error && error.message);
      return [message || "Something went wrong, please try again."];
    },

    groupPayload(form) {
      return {
        title: form.title,
        slug: form.slug,
        status: form.status,
        priority: form.priority,
      };
    },

    showGroupCreateModal() {
      this.groupCreateForm = { title: "", slug: "", status: 1, priority: 0, errors: [] };
      this.$modal.show("group-create-modal");
    },

    showGroupUpdateModal(group) {
      this.groupUpdateForm = {
        id: group.id,
        title: group.title || "",
        slug: group.slug || "",
        status: group.status === undefined || group.status === null ? 1 : group.status,
        priority: group.priority === undefined || group.priority === null ? 0 : group.priority,
        errors: [],
      };
      this.$modal.show("group-update-modal");
    },

    createGroup(event) {
      event.preventDefault();
      this.groupCreateForm.errors = [];

      if (!this.groupCreateForm.title.trim()) {
        this.groupCreateForm.errors = ["Title is required."];
        return;
      }

      this.groupCreateBtnDisabled = true;
      axios
        .post(this.groupsUrl, this.groupPayload(this.groupCreateForm))
        .then(() => {
          this.$modal.hide("group-create-modal");
          this.loadGroups();
        })
        .catch((error) => {
          this.groupCreateForm.errors = this.groupErrors(error);
        })
        .finally(() => {
          this.groupCreateBtnDisabled = false;
        });
    },

    updateGroup(event) {
      event.preventDefault();
      this.groupUpdateForm.errors = [];

      if (!this.groupUpdateForm.title.trim()) {
        this.groupUpdateForm.errors = ["Title is required."];
        return;
      }

      this.groupUpdateBtnDisabled = true;
      axios
        .put(this.groupsUrl + "/" + this.groupUpdateForm.id, this.groupPayload(this.groupUpdateForm))
        .then(() => {
          this.$modal.hide("group-update-modal");
          this.loadGroups();
        })
        .catch((error) => {
          this.groupUpdateForm.errors = this.groupErrors(error);
        })
        .finally(() => {
          this.groupUpdateBtnDisabled = false;
        });
    },

    deleteGroup() {
      if (!confirm("Delete group \"" + this.groupUpdateForm.title + "\"? Categories in this group will no longer be grouped.")) {
        return;
      }

      axios
        .delete(this.groupsUrl + "/" + this.groupUpdateForm.id)
        .then(() => {
          this.$modal.hide("group-update-modal");
          this.loadGroups();
        })
        .catch((error) => {
          this.groupUpdateForm.errors = this.groupErrors(error);
        });
    },
  },
};
</script>
<style src="../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.item {
  background-size: cover !important;
  color: #fff;
  min-height: 100px;
  border-radius: 6px;
  margin: 5px !important;
  cursor: pointer;
}
.item-overlay {
  background-color: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 15px 20px;
  transition: 0.3s !important;
}
.item-overlay:hover {
  background-color: rgba(0, 0, 0, 0.6);
}
.add-item {
  color: #666;
  border: 2px dashed #999;
  padding-top: 17px;
  transition: 0.3s !important;
}
.add-item:hover {
  color: #f42f4b;
  border-color: #f42f4b;
}
.alert{
  position: absolute;
  top: 0;
  right: 0;
}
ul{
  list-style-type: none;
}
li{
  line-height: 12px;
}

/* ── Category groups ── */
.groups-panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}
.groups-panel__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}
.groups-panel__title {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-1);
  display: flex;
  align-items: center;
  gap: 7px;
}
.groups-panel__title i {
  color: var(--accent);
}
.groups-panel__subtitle {
  font-size: 12px;
  color: var(--text-3);
  margin-top: 2px;
}
.groups-panel__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary-action {
  height: 36px;
  padding: 0 16px;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity .15s;
}
.btn-primary-action:hover { opacity: .88; }
.btn-primary-action:disabled { opacity: .5; cursor: not-allowed; }
.btn-secondary-action {
  height: 36px;
  padding: 0 14px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-2);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: border-color .15s, color .15s;
}
.btn-secondary-action:hover { border-color: var(--accent); color: var(--accent); }

.groups-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 22px 12px;
  border: 1px dashed var(--border-2);
  border-radius: 10px;
  font-size: 13px;
  color: var(--text-3);
  text-align: center;
}

.groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}
.group-card {
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color .15s, transform .15s;
}
.group-card:hover {
  border-color: var(--accent);
  transform: translateY(-1px);
}
.group-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.group-card__title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 11px;
  color: var(--text-3);
}
.group-card__footer {
  font-size: 11px;
  color: var(--text-3);
  display: flex;
  align-items: center;
  gap: 4px;
}
.group-card:hover .group-card__footer { color: var(--accent); }

.group-status {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}
.group-status--active   { background: rgba(34,197,94,.15);  color: #22c55e; border: 1px solid rgba(34,197,94,.4); }
.group-status--inactive { background: rgba(244,47,75,.15);  color: var(--accent); border: 1px solid rgba(244,47,75,.4); }

/* ── Group modals ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
}
.dd-modal__title { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close {
  width: 28px;
  height: 28px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color .15s;
}
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body { padding: 1.25rem; }
.dd-modal__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: .75rem 1.25rem;
  border-top: 1px solid var(--border);
}
.form-field { display: flex; flex-direction: column; gap: 5px; }
.mb-3 { margin-bottom: .85rem; }
.filter-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .4px;
}
.required { color: var(--accent); }
.field-hint { font-size: 11px; color: var(--text-3); }
.dd-input {
  height: 36px;
  background: var(--bg-base);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-1);
  font-size: 13px;
  padding: 0 10px;
  outline: none;
  width: 100%;
  transition: border-color .15s;
}
.dd-input:focus { border-color: var(--accent); }

.error-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  color: var(--accent);
  font-size: 12px;
}
.error-list li { line-height: 1.6; }

.btn-cancel {
  height: 36px;
  padding: 0 16px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-2);
  font-size: 13px;
  cursor: pointer;
}
.btn-submit {
  height: 36px;
  padding: 0 16px;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity .15s;
}
.btn-submit:hover { opacity: .88; }
.btn-submit:disabled { opacity: .5; cursor: not-allowed; }
.btn-delete {
  height: 36px;
  padding: 0 14px;
  background: rgba(244,47,75,.1);
  border: 1px solid rgba(244,47,75,.3);
  border-radius: 8px;
  color: var(--accent);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background .15s;
}
.btn-delete:hover { background: rgba(244,47,75,.2); }

@media (max-width: 768px) {
  .groups-panel__actions { width: 100%; }
  .groups-panel__actions .btn-primary-action,
  .groups-panel__actions .btn-secondary-action { flex: 1; justify-content: center; }
}

</style>